import type { Dictionary } from "../dictionary";

const en: Dictionary = {
  meta: {
    siteName: "Tetri Software",
    defaultTitle: "Tetri Copilot LLC — Intelligent Business Software",
    defaultDescription:
      "Tetri Copilot LLC builds the Tetri family of intelligent business software — Tetri Copilot and Tetri Engage — and delivers select software, AI and web development services.",
  },

  nav: {
    home: "Home",
    products: "Products",
    services: "Services",
    company: "Company",
    contact: "Contact",
    productsMenu: {
      copilot: "Tetri Copilot",
      copilotHint: "Finance & operations, simplified",
      engage: "Tetri Engage",
      engageHint: "Opportunities, prioritized",
      viewAll: "View all products",
    },
    cta: "Explore Products",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    languageLabel: "Language",
    skipToContent: "Skip to content",
  },

  footer: {
    tagline:
      "Tetri Copilot LLC builds intelligent software that helps businesses operate, engage, and grow with less complexity.",
    productsHeading: "Products",
    servicesHeading: "Services",
    companyHeading: "Company",
    legalHeading: "Legal",
    company: {
      about: "About",
      services: "Services",
      contact: "Contact",
      partnerships: "Partnerships",
    },
    legal: {
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      cookies: "Cookie Policy",
    },
    rights: "All rights reserved.",
    registeredNote: "Tetri Copilot LLC is registered in Georgia.",
  },

  productPanel: {
    copilotTitle: "Workspace overview",
    engageTitle: "Pipeline overview",
    copilotRows: [
      { label: "Invoice · Client A", status: "Sent" },
      { label: "Invoice · Client B", status: "Paid" },
      { label: "Expense · Travel", status: "Logged" },
    ],
    engageRows: [
      { label: "Opportunity · Signal A", status: "New" },
      { label: "Opportunity · Signal B", status: "Qualified" },
      { label: "Follow-up · Signal C", status: "Due today" },
    ],
    copilotScreenshotAlt:
      "Tetri Copilot dashboard showing revenue, outstanding invoices, net cash and a cash flow chart",
    engageScreenshotAlt:
      "Tetri Engage opportunity pipeline showing AI-scored leads and an automatically created follow-up action",
  },

  common: {
    buttons: {
      exploreProducts: "Explore Products",
      contactUs: "Contact Us",
      talkToUs: "Talk to Us",
      learnMore: "Learn More",
      visitProduct: "Visit Product",
      discussProject: "Discuss a Project",
      sendMessage: "Send Message",
      viewAllProducts: "View All Products",
      backHome: "Back to Home",
      viewServices: "View Services",
      readMore: "Read More",
    },
    badges: {
      live: "Live",
      beta: "Beta",
      comingSoon: "Coming Soon",
    },
    labels: {
      capabilities: "Capabilities",
      builtFor: "Built for",
      relatedProducts: "Related Products",
    },
  },

  seo: {
    home: {
      title: "Tetri Software — Intelligent Business Software for SMEs",
      description: "AI-enabled business software for growing companies in Georgia, the UAE, Saudi Arabia, the US and worldwide: Tetri Copilot and Tetri Engage.",
    },
    products: {
      title: "Business Software: Tetri Copilot & Tetri Engage | Tetri Software",
      description: "Compare Tetri Copilot for invoicing, expenses and compliance with Tetri Engage for lead discovery and follow-ups. Built for SMEs and growing teams.",
    },
    copilot: {
      title: "Tetri Copilot: Invoicing & Admin Software for SMEs | Tetri Software",
      description: "Invoicing, expenses, business records, compliance tracking and reporting in one AI-assisted workspace for small businesses in Georgia, the UAE, KSA and the US.",
    },
    engage: {
      title: "Tetri Engage: AI Lead Discovery & Follow-up | Tetri Software",
      description: "Find promising opportunities, prioritize leads with AI scoring and keep every follow-up on track. For sales teams in Georgia, the UAE, KSA, the US and beyond.",
    },
    services: {
      title: "Custom Software, AI & Web Development | Tetri Software",
      description: "SaaS development, AI automation, custom web apps and websites from the team behind Tetri Copilot and Tetri Engage, for clients in Georgia, the UAE, KSA and the US.",
    },
    about: {
      title: "About Tetri Copilot LLC | Tetri Software",
      description: "Tetri Copilot LLC is a software company registered in Georgia, building the Tetri product family for businesses in the UAE, Saudi Arabia, the US and worldwide.",
    },
    contact: {
      title: "Contact Tetri | Tetri Software",
      description: "Questions about Tetri Copilot, Tetri Engage, partnerships or a software project? Reach the Tetri team by email, phone or the contact form.",
    },
  },

  home: {
    hero: {
      heading: "Software built to make business simpler.",
      subheading:
        "Tetri builds focused business products. Tetri Copilot runs finance and administration, Tetri Engage finds and follows up on opportunities, and the team behind them takes on selected client projects.",
      ctaPrimary: "Explore Products",
      ctaSecondary: "Talk to Us",
    },
    stage: {
      copilot: {
        description: "Invoicing, expenses, records, compliance tracking and reporting in one workspace.",
        cta: "Explore Tetri Copilot",
      },
      engage: {
        description: "Lead discovery, opportunity scoring and follow-ups that do not slip.",
        cta: "Explore Tetri Engage",
      },
    },
    work: {
      heading: "Running a business creates more work than any team can keep up with.",
      body: "Each Tetri product takes one part of that work and turns it into a focused, intelligent workflow, so your team spends less time managing systems.",
      listLabel: "Where the work goes",
      items: [
        { problem: "Records, invoices and compliance tasks scattered across tools", product: "copilot" },
        { problem: "Reports rebuilt by hand every month", product: "copilot" },
        { problem: "Promising opportunities lost in the noise", product: "engage" },
        { problem: "Follow-ups that quietly fall through the cracks", product: "engage" },
      ],
    },
    copilot: {
      heading: "Business administration that stays out of your way.",
      body: "One modern workspace for the paperwork side of the business, with AI handling the repetitive parts.",
      capabilities: ["Invoicing", "Expenses", "Business records", "Compliance tracking", "Reporting", "AI assistance"],
      cta: "Explore Tetri Copilot",
    },
    engage: {
      heading: "From signal to follow-up, with less manual work.",
      body: "Tetri Engage helps growing teams find promising opportunities, decide which matter most, and keep every follow-up on track.",
      steps: [
        { title: "A signal appears", description: "Engage spots activity worth a closer look." },
        { title: "The opportunity is scored", description: "AI ranks it so the team works the strongest leads first." },
        { title: "A follow-up is created", description: "The next action lands on the right person's list." },
      ],
      cta: "Explore Tetri Engage",
    },
    ai: {
      heading: "AI should remove work, not create another system to manage.",
      body: "Both products share one approach: AI does a specific job inside the workflow you already have.",
      points: [
        { title: "Assist", description: "Suggests next actions on your records and drafts the routine parts, so tasks start half done." },
        { title: "Classify", description: "Sorts and tags records and opportunities so they land in the right place without manual filing." },
        { title: "Prioritize", description: "Surfaces what needs attention first, from a due compliance task to the strongest lead." },
      ],
    },
    services: {
      heading: "The same product thinking, applied to your project.",
      body: "The team that builds Tetri Copilot and Tetri Engage takes on a small number of client projects, held to the same engineering standards.",
      cta: "Discuss a Project",
      items: [
        { title: "Custom software and SaaS", description: "Portals, internal systems, dashboards and SaaS products, built from scratch." },
        { title: "AI and automation", description: "AI features inside your existing software, plus automation for repetitive workflows." },
        { title: "Websites and design systems", description: "Custom-coded marketing sites and reusable design systems, fast and SEO-ready." },
        { title: "Integrations and advisory", description: "API and payment integrations, cloud architecture and technical modernization." },
      ],
      processLabel: "How a project runs",
      process: [
        { title: "Discover", description: "Scope the problem and the outcome." },
        { title: "Design", description: "Shape the product before code." },
        { title: "Build", description: "Ship in reviewed increments." },
        { title: "Support", description: "Run, measure and improve." },
      ],
    },
    company: {
      heading: "A product company, built to serve businesses worldwide.",
      facts: [
        { value: "2", label: "Products live in market" },
        { value: "4", label: "Languages: English, Arabic, Georgian, Russian" },
        { value: "Global", label: "Priority markets: Georgia, the UAE, Saudi Arabia and the US" },
        { value: "Georgia", label: "Tetri Copilot LLC, registered in Georgia" },
      ],
      trust: [
        { title: "Secure cloud software.", description: "Built on modern, maintained cloud infrastructure." },
        { title: "Privacy-aware design.", description: "Each product collects only what it genuinely needs." },
        { title: "Transparent subscriptions.", description: "Clear pricing and no hidden terms." },
      ],
    },
    finalCta: {
      heading: "Find the Tetri product that fits your next stage of growth.",
      body: "Explore Tetri Copilot and Tetri Engage, or tell us what your business needs next.",
      ctaPrimary: "Explore Products",
      ctaSecondary: "Contact Tetri",
    },
  },

  about: {
    hero: {
      eyebrow: "Company",
      heading: "The company behind the Tetri product family.",
      subheading:
        "Tetri Copilot LLC designs, builds, and operates intelligent business software — and provides select development services to a small number of customers.",
    },
    statement: {
      heading: "What we do",
      body: [
        "Tetri Copilot LLC is a software product company. We build, own, and operate the Tetri family of business applications, starting with Tetri Copilot and Tetri Engage.",
        "We also work with select customers on custom software, AI, and web development projects — applying the same product discipline we use for our own software.",
      ],
    },
    builds: {
      heading: "What Tetri builds",
      body: "Every Tetri product focuses on a specific area of business operations, built on a shared approach to simplicity, intelligent automation, and modern software design.",
      points: [
        { title: "Tetri Copilot", description: "AI-enabled business administration, finance operations, and compliance for SMEs." },
        { title: "Tetri Engage", description: "AI-enabled lead discovery, prioritization, and follow-up for growing teams." },
      ],
    },
    vision: {
      heading: "Why Tetri exists",
      body: "Growing businesses accumulate operational work faster than they can hire for it — scattered records, missed follow-ups, and automation that adds complexity instead of removing it. Tetri exists to turn that fragmented work into focused, intelligent software that a small team can actually run.",
    },
    principles: {
      eyebrow: "How we build",
      heading: "Principles behind every Tetri product.",
      items: [
        { title: "Practical Intelligence", description: "AI should reduce work, not create more systems to manage." },
        { title: "Simplicity", description: "Complex operational problems should become understandable software." },
        { title: "Useful Automation", description: "Automation should remove repetitive work where it creates real value." },
        { title: "Quality", description: "Design, engineering, performance, and reliability matter in every release." },
        { title: "Trust", description: "Business software should be dependable, secure, and transparent." },
        { title: "Continuous Improvement", description: "Products evolve based on real customer needs, not guesswork." },
      ],
    },
    international: {
      heading: "Where we work",
      body: "Tetri is built as an international product family, with a focus on these markets.",
      markets: [
        { code: "GE", name: "Georgia", note: "Home market and operating base" },
        { code: "AE", name: "United Arab Emirates", note: "Priority growth market" },
        { code: "SA", name: "Saudi Arabia", note: "Priority growth market" },
        { code: "US", name: "United States", note: "Priority growth market" },
        { code: "GCC", name: "Wider GCC", note: "Expanding regional focus" },
        { code: "Global", name: "Worldwide", note: "Available to businesses in any country" },
      ],
    },
    technology: {
      heading: "How we build",
      body: "Every Tetri product is built on modern, maintainable technology — designed to stay reliable as the product family grows.",
      points: [
        "Modern web applications",
        "Cloud-based, scalable architecture",
        "API-driven integrations",
        "AI applied where it creates measurable value",
        "Security and privacy considered from the start",
      ],
    },
    legal: {
      heading: "Corporate information",
      body: "Tetri Copilot LLC is the legal entity behind the Tetri product family and the services described on this website.",
      entityLabel: "Legal entity",
      entityValue: "Tetri Copilot LLC",
      jurisdictionLabel: "Registered in",
      jurisdictionValue: "Georgia",
      contactLabel: "Business inquiries",
    },
    cta: {
      heading: "Want to know more about Tetri?",
      body: "Explore what we build, or get in touch directly.",
      ctaPrimary: "Explore Products",
      ctaSecondary: "Contact Us",
    },
  },

  products: {
    compare: {
      heading: "Which Tetri product fits?",
      focusLabel: "Main job",
      audienceLabel: "Built for",
      outcomeLabel: "What changes",
      capabilitiesLabel: "Capabilities",
    },
    hero: {
      eyebrow: "Products",
      heading: "The Tetri product family.",
      subheading:
        "Two focused products, built and operated by Tetri Copilot LLC, sharing one approach to simplicity, intelligent automation, and modern software design.",
    },
    list: [
      {
        name: "Tetri Copilot",
        tagline: "Finance & operations, simplified",
        outcome: "Less time on paperwork, more time on the business.",
        description:
          "AI-enabled business administration for SMEs — invoicing, expenses, records, compliance tracking, and reporting in one modern workspace.",
        capabilities: ["Invoicing", "Expenses", "Business records", "Compliance tracking", "Reporting", "AI assistance"],
        audience: "SME owners, finance and operations teams",
        ctaLabel: "Explore Tetri Copilot",
      },
      {
        name: "Tetri Engage",
        tagline: "Opportunities, prioritized",
        outcome: "Fewer opportunities lost to a full inbox.",
        description:
          "AI-enabled lead discovery and engagement for growing teams — surface opportunities, prioritize what matters, and keep follow-ups organized.",
        capabilities: ["Lead discovery", "Signals", "Prioritization", "Follow-ups", "Engagement workflows", "AI assistance"],
        audience: "Sales and customer-facing teams",
        ctaLabel: "Explore Tetri Engage",
      },
    ],
    philosophy: {
      heading: "One family, one philosophy",
      body: "Each Tetri product focuses on a specific area of business operations while following a shared approach to simplicity, intelligent automation, and modern software design — so the experience feels consistent no matter which product you use.",
    },
    future: {
      heading: "Built to grow",
      body: "The Tetri product architecture is designed to support future products as they're announced, without redesigning how the family works together.",
    },
    cta: {
      heading: "Ready to see a Tetri product up close?",
      body: "Visit the dedicated product website for full details, or contact us with questions.",
      ctaLabel: "Contact Tetri",
    },
  },

  copilotPage: {
    eyebrow: "Tetri Copilot",
    heading: "Business administration that stays out of your way.",
    intro:
      "Tetri Copilot helps small and growing businesses organize invoicing, expenses, records, compliance activities, reporting, and day-to-day administration in one modern workspace.",
    problemHeading: "The problem",
    problemBody:
      "Business administration tends to spread across spreadsheets, inboxes, and disconnected tools — invoices go untracked, expenses are logged too late, and compliance deadlines get discovered at the last minute. Tetri Copilot brings that work into one place.",
    capabilitiesHeading: "What Tetri Copilot does",
    capabilities: [
      { title: "Invoicing", description: "Create, send, and track invoices without leaving your workspace." },
      { title: "Expenses", description: "Capture and organize business expenses as they happen." },
      { title: "Business records", description: "Keep core business information organized and easy to find." },
      { title: "Compliance tracking", description: "Stay on top of recurring compliance activities and deadlines." },
      { title: "Reporting", description: "Turn day-to-day records into clear, usable reports." },
      { title: "AI assistance", description: "AI helps find, classify, and draft — reducing manual admin work." },
    ],
    outcomesHeading: "What changes",
    outcomes: [
      "Business records live in one organized workspace instead of scattered tools",
      "Invoicing and expense tracking take minutes, not hours",
      "Compliance deadlines are visible before they become urgent",
      "Reporting reflects real, up-to-date information",
    ],
    aiHeading: "The role of AI",
    aiBody:
      "Tetri Copilot uses AI to assist with day-to-day administration — finding records, classifying expenses, and drafting routine documents — so the team spends less time on manual data entry.",
    audienceHeading: "Who it's for",
    audiences: ["SME owners and managers", "Finance and operations teams", "Growing businesses replacing spreadsheets"],
    scopeNote:
      "Tetri Copilot organizes business records, invoicing, and compliance tracking. It does not hold or process customer funds, act as a bank or payment institution, or provide lending or investment advice.",
    ctaHeading: "See Tetri Copilot in full detail",
    ctaBody: "This page is a corporate overview. Visit the dedicated Tetri Copilot website for the complete product experience.",
    ctaLabel: "Visit Tetri Copilot",
    relatedHeading: "Related products",
  },

  engagePage: {
    flowHeading: "How Tetri Engage works",
    eyebrow: "Tetri Engage",
    heading: "From signal to follow-up, with less manual work.",
    intro:
      "Tetri Engage helps growing teams discover promising opportunities, prioritize what matters, organize follow-ups, and reduce repetitive customer-engagement work.",
    problemHeading: "The problem",
    problemBody:
      "Promising opportunities get lost in the noise of a busy inbox, and manual follow-up doesn't scale as a team grows. Tetri Engage turns scattered signals into an organized, prioritized pipeline.",
    capabilitiesHeading: "What Tetri Engage does",
    capabilities: [
      { title: "Lead discovery", description: "Surface promising opportunities before they get lost." },
      { title: "Signals", description: "Turn relevant activity into actionable signals." },
      { title: "Opportunity prioritization", description: "See what deserves attention first." },
      { title: "Follow-ups", description: "Keep every follow-up organized and on schedule." },
      { title: "Engagement workflows", description: "Structure outreach without manual busywork." },
      { title: "AI assistance", description: "AI helps qualify, prioritize, and draft outreach." },
    ],
    outcomesHeading: "What changes",
    outcomes: [
      "Opportunities are discovered and organized instead of missed",
      "Teams know what to follow up on first",
      "Follow-ups happen on schedule, not by memory",
      "Less repetitive manual outreach work",
    ],
    aiHeading: "The role of AI",
    aiBody:
      "Tetri Engage uses AI to help qualify opportunities, prioritize what matters, and draft follow-up communication — with the team always in control of what gets sent.",
    audienceHeading: "Who it's for",
    audiences: ["Sales and customer-facing teams", "Growing teams without a dedicated ops function", "Teams that rely on manual follow-up today"],
    scopeNote:
      "Tetri Engage supports lead discovery and organized follow-up. It does not guarantee sales outcomes or lead quality, perform unrestricted data scraping, or send fully autonomous outreach without team oversight.",
    ctaHeading: "See Tetri Engage in full detail",
    ctaBody: "This page is a corporate overview. Visit the dedicated Tetri Engage website for the complete product experience.",
    ctaLabel: "Visit Tetri Engage",
    relatedHeading: "Related products",
  },

  services: {
    hero: {
      eyebrow: "Services",
      heading: "The same product thinking, applied to your project.",
      subheading:
        "Tetri Copilot LLC builds Tetri Copilot and Tetri Engage as its own products — and brings that same engineering discipline to a select number of customer projects.",
    },
    categories: [
      {
        slug: "saas-development",
        title: "SaaS Product Development",
        summary: "End-to-end design and development of SaaS applications, from first release to ongoing iteration.",
        outcomes: ["A production-ready SaaS product", "Architecture built to scale with real usage", "A maintainable codebase your team can grow"],
      },
      {
        slug: "ai-automation",
        title: "AI Enablement & Automation",
        summary: "Practical AI features and workflow automation added to new or existing software.",
        outcomes: ["AI assistance embedded in real workflows", "Automation for genuinely repetitive tasks", "Intelligent search, classification, and prioritization"],
      },
      {
        slug: "web-development",
        title: "Custom Web Application Development",
        summary: "Portals, dashboards, and internal tools built around how your business actually works.",
        outcomes: ["A tool designed around your workflow, not a template", "Clean integration with existing systems", "An interface your team will actually use"],
      },
      {
        slug: "business-automation",
        title: "Business-Process Automation",
        summary: "Removing repetitive manual work from operational processes with targeted automation.",
        outcomes: ["Fewer manual, repetitive steps", "Processes that run consistently", "Time redirected to higher-value work"],
      },
      {
        slug: "website-development",
        title: "Website Design & Development",
        summary: "Custom-coded, high-performance business and SaaS websites built for credibility and conversion.",
        outcomes: ["A fast, professional, credible website", "A reusable design system for future pages", "SEO-ready structure from day one"],
      },
      {
        slug: "consulting-support",
        title: "Software Consulting & Technical Support",
        summary: "Technical advisory, architecture reviews, and ongoing support for existing software.",
        outcomes: ["Clear, practical technical direction", "Architecture reviewed by an experienced product team", "Ongoing support you can rely on"],
      },
    ],
    approach: {
      eyebrow: "How we work",
      heading: "A product mindset, applied to your project.",
      body: "We approach every engagement the way we approach our own products — starting from the outcome, not the feature list.",
      steps: [
        { title: "Understand", description: "We start by understanding the business problem, not just the technical brief." },
        { title: "Design & build", description: "We design and build with the same standards behind Tetri Copilot and Tetri Engage." },
        { title: "Support", description: "We stay involved after launch, so the software keeps working as your business changes." },
      ],
    },
    proof: {
      heading: "Built by the team behind Tetri Copilot and Tetri Engage",
      body: "Our services capability comes directly from building and operating our own products — the same engineering standards, applied to your project.",
    },
    faq: {
      heading: "Common questions",
      items: [
        {
          question: "Does Tetri Copilot LLC take on every project?",
          answer: "No. We work with a select number of customers at a time so every project gets real attention from our product team.",
        },
        {
          question: "Is this the same team that builds Tetri Copilot and Tetri Engage?",
          answer: "Yes. Services are delivered by the same team and engineering standards behind our own products.",
        },
        {
          question: "Can services integrate with Tetri Copilot or Tetri Engage?",
          answer: "In many cases, yes — get in touch and we can discuss what makes sense for your setup.",
        },
      ],
    },
    cta: {
      heading: "Have a project in mind?",
      body: "Tell us about it and we'll let you know if it's a good fit.",
      ctaLabel: "Discuss a Project",
    },
  },

  contact: {
    hero: {
      eyebrow: "Contact",
      heading: "Let's talk about what you need.",
      subheading: "Product questions, partnership ideas, or a project you'd like to discuss — we'd like to hear from you.",
    },
    reasons: [
      { title: "Product inquiries", description: "Questions about Tetri Copilot or Tetri Engage." },
      { title: "Partnerships", description: "Referral, reseller, or technology partnership opportunities." },
      { title: "Services", description: "Software, AI, or website development projects." },
      { title: "Everything else", description: "Media, company questions, or anything not listed above." },
    ],
    info: {
      heading: "Direct contact",
      emailLabel: "Email",
      phoneLabel: "Phone",
      phones: ["+995 55 000 4769", "+995 511 415 313"],
      locationLabel: "Based in",
      locationValue: "Georgia — serving Georgia, the UAE, Saudi Arabia, the US and worldwide",
    },
    form: {
      heading: "Send us a message",
      nameLabel: "Name",
      namePlaceholder: "Your full name",
      emailLabel: "Work email",
      emailPlaceholder: "you@company.com",
      companyLabel: "Company",
      companyPlaceholder: "Your company name",
      inquiryTypeLabel: "Inquiry type",
      inquiryTypePlaceholder: "Select an inquiry type",
      inquiryTypes: [
        { value: "product", label: "Product inquiry" },
        { value: "sales-demo", label: "Sales / demo" },
        { value: "partnership", label: "Partnership" },
        { value: "software-development", label: "Software development" },
        { value: "ai-automation", label: "AI / automation" },
        { value: "website-development", label: "Website development" },
        { value: "media", label: "Media / company" },
        { value: "other", label: "Other" },
      ],
      countryLabel: "Country (optional)",
      countryPlaceholder: "Your country",
      messageLabel: "Message",
      messagePlaceholder: "Tell us a little about what you need.",
      privacyNotice: "By submitting, you agree to our Privacy Policy. We only use your details to respond to your inquiry.",
      submit: "Send Message",
      submitting: "Sending…",
      successHeading: "Message sent.",
      successBody: "Thank you — we've received your message and will get back to you soon.",
      errorHeading: "Something went wrong.",
      errorBody: "Your message wasn't sent. Please try again, or email us directly.",
      retry: "Try Again",
      devModeHeading: "Contact form not yet connected",
      devModeBody:
        "This environment has no contact-form endpoint configured, so submissions can't be sent. Set VITE_CONTACT_FORM_ENDPOINT (see .env.example) to enable it.",
      validation: {
        required: "This field is required.",
        invalidEmail: "Enter a valid email address.",
        tooShort: "Please add a little more detail.",
      },
    },
  },

  legal: {
    privacy: {
      title: "Privacy Policy",
      updated: "Last updated: September 2026",
      intro:
        "This policy explains what information Tetri Copilot LLC collects through this website and how it is used. It applies to tetricopilot.com's corporate website only; Tetri Copilot and Tetri Engage products may maintain their own privacy notices.",
      sections: [
        {
          title: "Information we collect",
          description: "When you submit our contact form, we collect the information you provide — such as your name, work email, company, and message. We do not require an account to browse this website.",
        },
        {
          title: "How we use information",
          description: "We use contact form submissions only to respond to your inquiry and to keep a record of business communications. We do not sell personal information to third parties.",
        },
        {
          title: "Cookies and analytics",
          description: "This website may use essential cookies for core functionality and privacy-aware analytics to understand overall site usage. See our Cookie Policy for details.",
        },
        {
          title: "Data retention",
          description: "We retain contact submissions for as long as reasonably necessary to respond to and document business inquiries.",
        },
        {
          title: "Your rights",
          description: "You may request access to, correction of, or deletion of information you've submitted to us by emailing info@tetricopilot.com.",
        },
        {
          title: "Contact",
          description: "Questions about this policy can be sent to info@tetricopilot.com.",
        },
      ],
    },
    terms: {
      title: "Terms of Service",
      updated: "Last updated: September 2026",
      intro:
        "These terms govern your use of the Tetri Copilot LLC corporate website. Use of individual Tetri products (Tetri Copilot, Tetri Engage) is governed by their own terms, available on their respective websites.",
      sections: [
        {
          title: "Use of this website",
          description: "This website is provided to introduce Tetri Copilot LLC, the Tetri product family, and our services. You agree to use it only for lawful purposes.",
        },
        {
          title: "Intellectual property",
          description: "The Tetri name, logo, and website content are the property of Tetri Copilot LLC and may not be used without permission.",
        },
        {
          title: "No warranty",
          description: "This website and its content are provided \"as is\" without warranties of any kind, to the extent permitted by law.",
        },
        {
          title: "Limitation of liability",
          description: "Tetri Copilot LLC is not liable for indirect or consequential damages arising from your use of this website, to the extent permitted by law.",
        },
        {
          title: "Changes to these terms",
          description: "We may update these terms from time to time. Continued use of the website constitutes acceptance of the current terms.",
        },
        {
          title: "Contact",
          description: "Questions about these terms can be sent to info@tetricopilot.com.",
        },
      ],
    },
    cookies: {
      title: "Cookie Policy",
      updated: "Last updated: September 2026",
      intro: "This policy explains how Tetri Copilot LLC uses cookies and similar technologies on this website.",
      sections: [
        {
          title: "What cookies we use",
          description: "We use essential cookies required for the website to function correctly, and may use privacy-aware analytics cookies to understand overall site usage.",
        },
        {
          title: "Managing cookies",
          description: "Most browsers let you control or disable cookies through their settings. Disabling essential cookies may affect how parts of this website function.",
        },
        {
          title: "Third-party cookies",
          description: "We do not permit third-party advertising cookies on this website.",
        },
        {
          title: "Contact",
          description: "Questions about this policy can be sent to info@tetricopilot.com.",
        },
      ],
    },
  },

  notFound: {
    eyebrow: "404",
    heading: "This page doesn't exist.",
    body: "The page you're looking for may have moved or never existed. Here are a few useful places to go instead.",
    ctaHome: "Home",
    ctaProducts: "Products",
    ctaContact: "Contact",
  },
};

export default en;
